import React from 'react';

import logger from '@app/core-features/logger/logger';

import { useMetaDataState } from '@/core/features/a-dynamic-page/hooks/meta-data-state';
import { useCookiesMapped } from '@/core/features/app/app-atoms';
import { useDeviceoutput } from '@/core/features/cookies/use-device-output';
import { fireGeneralTracking, fireGeneralTrackingPixels } from '@/core/features/tracking/c24-general-tracking';
import { useCartLocalStorage } from '@/features/cart/service/cart-state';

export function useCheckoutTracking() {
    const { deviceoutput } = useDeviceoutput();
    const metaData = useMetaDataState();
    const cookiesMapped = useCookiesMapped();
    const { cartStorage, isCartReady, markCartAsTracked, markCheckoutAsTracked, markCheckoutSummaryAsTracked } =
        useCartLocalStorage({
            activitySpotId: metaData?.trackingMeta.activitySpotId ?? null,
            schoolId: metaData?.trackingMeta.schoolId ?? null,
        });

    const trackCheckout = React.useCallback(() => {
        if (!isCartReady) {
            return;
        }

        if ((cartStorage.wasTrackedUntil.checkout ?? 0) > Date.now()) {
            return; // checkout was already tracked
        }

        if (!metaData) {
            logger.error('Unable to track checkout. metaData is not defined');
            return;
        }

        markCheckoutAsTracked();
        fireGeneralTracking({
            cookiesMapped,
            deviceoutput,
            overrides: {
                actionId: metaData.trackingMeta.actionId,
                areaId: metaData.trackingMeta.areaId,
            },
            trackingMeta: metaData.trackingMeta,
        });
    }, [
        cartStorage.wasTrackedUntil.checkout,
        cookiesMapped,
        deviceoutput,
        isCartReady,
        markCheckoutAsTracked,
        metaData,
    ]);

    const trackCartOverlay = React.useCallback(
        ({ actionId, areaId }: { actionId: string; areaId: string }) => {
            if (!isCartReady) {
                return;
            }

            if ((cartStorage.wasTrackedUntil.cart ?? 0) > Date.now()) {
                return; // cart was already tracked
            }

            if (!metaData) {
                logger.error('Unable to track cart overlay. metaData is not defined');
                return;
            }

            markCartAsTracked();
            fireGeneralTracking({
                cookiesMapped,
                deviceoutput,
                overrides: { actionId: actionId, areaId: areaId },
                trackingMeta: metaData.trackingMeta,
            });
        },
        [cartStorage.wasTrackedUntil.cart, cookiesMapped, deviceoutput, isCartReady, markCartAsTracked, metaData],
    );

    const trackSummary = React.useCallback(() => {
        if (!isCartReady) {
            return;
        }

        if ((cartStorage.wasTrackedUntil.summary ?? 0) > Date.now()) {
            return; // summary was already tracked
        }

        if (!metaData || metaData.trackingMeta.generalTrackingPixels.length === 0) {
            logger.error('Unable to track summary. metaData is not defined or generalTrackingPixels is empty');
            return;
        }

        markCheckoutSummaryAsTracked();
        fireGeneralTrackingPixels({
            cookiesMapped,
            deviceoutput,
            trackingMeta: metaData.trackingMeta,
        });
    }, [
        cartStorage.wasTrackedUntil.summary,
        cookiesMapped,
        deviceoutput,
        isCartReady,
        markCheckoutSummaryAsTracked,
        metaData,
    ]);

    return {
        trackCartOverlay,
        trackCheckout,
        trackSummary,
    };
}
